import React, { memo, useEffect } from 'react';
import { NodeProps, Position, useUpdateNodeInternals } from '@xyflow/react';
import { useSelector } from 'react-redux';
import BaseNode from '../BaseNode';
import { resolveEndpoint, summarize } from '../../utils/transformLoad';
import { checkTransformLoad } from '../../utils/transformLoadCheck';
import i18n from '../../../../i18n';
import { typeLabelOf } from '../../nodeCatalog';
import { ERROR_HANDLE_ID, ERROR_HANDLE_STYLE, hasErrorFlow, isErrorEdge } from '../../utils/errorFlow';

const icon = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path d="M4 7h11" /><path d="M12 4l3 3-3 3" />
    <path d="M20 17H9" /><path d="M12 14l-3 3 3 3" />
  </svg>
);

/** 데이터처리 — 소스(SAM파일/DB테이블/내부데이터) → (Move | 항목 매핑 및 변환) → 타깃(SAM파일/DB테이블/내부데이터) */
const TransformLoadNode: React.FC<NodeProps<any>> = ({ data, selected, id, type, ...props }) => {
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const subtitle = summarize(id, data?.config, nodes, edges);
  // 속성 패널의 '점검' 중 **오류만** 노드에 '!' 로 띄운다 — 실행하면 실패하거나 데이터가 사라지는 것들.
  // (SAM파일 항목은 서버에서 읽어야 알 수 있어 여기서는 노드가 가진 정보로만 본다 — 모르면 건너뛴다)
  const source = resolveEndpoint(id, 'up', nodes, edges);
  const target = resolveEndpoint(id, 'down', nodes, edges);
  const errors = checkTransformLoad({
    source, target, sourceColumns: source?.columns || [], targetColumns: target?.columns || [], cfg: data?.config || {},
  }).filter((i) => i.level === 'error');
  const warning = errors.length ? `${i18n.t('workflow:tl.checkErrors', { n: errors.length, first: errors[0].message })}${errors.length > 1 ? i18n.t('workflow:tl.more') : ''}` : undefined;
  // 예외 흐름을 켜면 정상 흐름 핸들 조금 아래에 빨간 핸들이 하나 더 (utils/errorFlow)
  const errorFlow = hasErrorFlow(id, data?.config, edges);
  // 핸들이 생기거나 사라지면 React Flow 에 알린다 — 알리지 않으면 '핸들을 찾을 수 없다'며 그 엣지를 그리지 않는다
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => { updateNodeInternals(id); }, [errorFlow, id, updateNodeInternals]);
  const handles = errorFlow
    ? { extraHandles: [{ id: ERROR_HANDLE_ID, type: 'source' as const, position: Position.Right, className: 'error-handle', style: ERROR_HANDLE_STYLE }] }
    : undefined;
  return (
    <BaseNode id={id} data={data} selected={selected} type={type} icon={icon} handles={handles}
      errorStub={errorFlow && !edges.some((e: any) => e.source === id && isErrorEdge(e))}
      label={data?.label || typeLabelOf('transformLoad')} subtitle={subtitle} warning={warning} {...props} />
  );
};

export default memo(TransformLoadNode);
